// ─────────────────────────────────────────────────────────
//  FAQ avto-taklif: fuqaro matniga eng mos javoblarni topish
//  Kirill ↔ lotin farqini translit orqali tekislaydi
// ─────────────────────────────────────────────────────────
const CYR = {
  а:'a', б:'b', в:'v', г:'g', д:'d', е:'e', ё:'yo', ж:'j', з:'z',
  и:'i', й:'y', к:'k', л:'l', м:'m', н:'n', о:'o', п:'p', р:'r',
  с:'s', т:'t', у:'u', ф:'f', х:'h', ц:'s', ч:'ch', ш:'sh', щ:'sh',
  ъ:'', ь:'', э:'e', ю:'yu', я:'ya', ў:'o', қ:'q', ғ:'g', ҳ:'h',
}

export function normalize(text) {
  if (!text) return ''
  let res = text.toLowerCase()
  
  // Transliterate multi-character Cyrillic letters first
  const multiCyr = {
    'ё': 'yo', 'ж': 'j', 'ц': 'ts', 'ч': 'ch', 'ш': 'sh', 'щ': 'sh', 
    'ю': 'yu', 'я': 'ya', 'ў': 'o', 'ғ': 'g'
  }
  res = res.replace(/[ёжцчшщюяўғ]/g, ch => multiCyr[ch])
  
  // Transliterate single Cyrillic letters
  res = res.replace(/[а-яқҳ]/g, ch => CYR[ch] ?? ch)
  
  // Clean quotes/apostrophes
  res = res.replace(/[''`ʻ’]/g, '')
  
  // Map similar Latin sequences/variants for extreme flexibility
  res = res.replace(/zh/g, 'j')
  res = res.replace(/kh/g, 'h')
  res = res.replace(/ts/g, 's')
  res = res.replace(/x/g, 'h') // Match 'x' and 'h' (e.g. xotin/hotin, poxojie/pohojie)
  res = res.replace(/w/g, 'v')
  res = res.replace(/q/g, 'k') // Match 'q' and 'k' (e.g. qizlar/kizlar)
  
  // Collapse whitespace
  return res.replace(/\s+/g, ' ').trim()
}

const STOP = new Set([
  // Uzbek
  'va','yoki','ham','uchun','bilan','menga','mening','sizga','qanday',
  'nima','deb','edi','bu','shu','men','siz','u','biz','ular','bir',
  // Russian
  'the','and','or','in','on','at','to','of','is',
  'i','v','na','ne','ni','da','ta','ili','kak','chto','eto','vot',
])

/** Tokenise and filter stopwords */
function tokens(text) {
  return normalize(text)
    .split(/[^a-z0-9]+/)
    .filter(w => w.length > 2 && !STOP.has(w))
}

/** Generate character bigrams for fuzzy matching */
function bigrams(word) {
  const bg = new Set()
  for (let i = 0; i < word.length - 1; i++) bg.add(word[i] + word[i + 1])
  return bg
}

/** Bigram similarity between two words (Dice coefficient) */
function bigramSim(a, b) {
  if (!a || !b) return 0
  const ba = bigrams(a)
  const bb = bigrams(b)
  let intersect = 0
  for (const bg of ba) if (bb.has(bg)) intersect++
  return (2 * intersect) / (ba.size + bb.size)
}

/**
 * Score a single item against a query.
 * Returns a numeric score (higher = better match).
 */
function scoreItem(it, q, qToks) {
  const nq = normalize(q)
  const nQuestion = normalize(it.question)
  const nAnswer = normalize(it.answer)
  let score = 0

  // ── 1. Exact substring match in question (highest priority) ────────────
  if (nq.length > 2 && nQuestion.includes(nq)) score += 20
  else if (nq.length > 2 && nAnswer.includes(nq)) score += 8

  // ── 2. Prefix match: query starts with or is prefix of question words ──
  const qWords = tokens(q)
  const itQWords = tokens(it.question)
  const itAWords = tokens(it.answer)

  for (const qw of qWords) {
    for (const iw of itQWords) {
      if (iw === qw) { score += 4; continue }              // exact token
      if (iw.startsWith(qw) && qw.length >= 3) score += 2.5  // prefix in question
      if (qw.startsWith(iw) && iw.length >= 3) score += 1.5  // query extends item word
    }
    for (const iw of itAWords) {
      if (iw === qw) { score += 1.5; continue }
      if (iw.startsWith(qw) && qw.length >= 3) score += 0.8
    }
  }

  // ── 3. Fuzzy / bigram similarity for each query token ─────────────────
  for (const qw of qWords) {
    if (qw.length < 4) continue                           // skip short tokens
    let bestQSim = 0, bestASim = 0
    for (const iw of itQWords) bestQSim = Math.max(bestQSim, bigramSim(qw, iw))
    for (const iw of itAWords) bestASim = Math.max(bestASim, bigramSim(qw, iw))
    if (bestQSim >= 0.65) score += bestQSim * 3
    else if (bestQSim >= 0.45) score += bestQSim * 1.5
    if (bestASim >= 0.65) score += bestASim * 1
  }

  // ── 4. Multi-word phrase bonus ─────────────────────────────────────────
  for (const phrase of nQuestion.split(/[,.?!;]/)) {
    const p = phrase.trim()
    if (p.length > 8 && nq.includes(p)) score += 10
  }

  // ── 5. Coverage bonus: what fraction of query tokens matched ──────────
  if (qToks.size > 1) {
    let matched = 0
    for (const qw of qToks) {
      const anyMatch = [...itQWords, ...itAWords].some(
        iw => iw === qw || iw.startsWith(qw) || bigramSim(qw, iw) >= 0.6
      )
      if (anyMatch) matched++
    }
    const coverage = matched / qToks.size
    if (coverage > 0.5) score += coverage * 5
  }

  return score
}

/**
 * items: [{id, question, answer, type}] — question/answer raw text
 * queryText: citizen's last message or manual search query
 * Returns: descending by score, only score > 0
 */
export function suggest(queryText, items, topN = 4) {
  const q = normalize(queryText)
  if (!q || q.length < 2) return []
  const qToks = new Set(tokens(queryText))

  const scored = items
    .map(it => ({ ...it, score: scoreItem(it, queryText, qToks) }))
    .filter(x => x.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, topN)

  return scored
}

/**
 * Highlight all occurrences of the query inside text.
 * Returns an array of {text, highlight} segments.
 */
export function highlightSegments(text, query) {
  if (!text || !query || query.trim().length < 2) return [{ text, highlight: false }]
  
  const nq = normalize(query)
  if (!nq) return [{ text, highlight: false }]
  
  const originalChars = Array.from(text)
  const mappings = []
  
  let normText = ''
  for (let i = 0; i < originalChars.length; i++) {
    const char = originalChars[i]
    const normChar = normalize(char)
    const start = normText.length
    normText += normChar
    const end = normText.length
    mappings.push({ start, end })
  }
  
  const segments = []
  let pos = 0
  let idx = normText.indexOf(nq)
  
  while (idx !== -1) {
    let origStart = -1
    let origEnd = -1
    
    for (let i = 0; i < mappings.length; i++) {
      const m = mappings[i]
      if (m.end > idx && m.start < idx + nq.length) {
        if (origStart === -1) origStart = i
        origEnd = i + 1
      }
    }
    
    if (origStart !== -1 && origEnd !== -1) {
      if (origStart > pos) {
        segments.push({ text: originalChars.slice(pos, origStart).join(''), highlight: false })
      }
      segments.push({ text: originalChars.slice(origStart, origEnd).join(''), highlight: true })
      pos = origEnd
    } else {
      break
    }
    
    idx = normText.indexOf(nq, idx + nq.length)
  }
  
  if (pos < originalChars.length) {
    segments.push({ text: originalChars.slice(pos).join(''), highlight: false })
  }
  
  return segments.length > 0 ? segments : [{ text, highlight: false }]
}


